import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShiftsService } from '../services/supabase/shiftsService';
import { CareReportSubmissionService } from '../services/supabase/careReportSubmissionService';
import { mapShiftRow } from '../utils/shiftUtils';
import { determinePriceByDate, minsInSpanEdited, hoursInSpan } from '../utils/hoursUtils';
import { QuickBooksService } from '../services/quickbooks/quickbooksService';
import './payrollPage.css';

function formatDatetime(datetimeStr) {
  if (!datetimeStr) return '—';
  return new Date(datetimeStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', meridiem: true,
  });
}

function formatDate(datetimeStr) {
  if (!datetimeStr) return '—';
  return new Date(datetimeStr).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function PayrollPage({ clientList, caretakerList, draft, onDraftChange, qbConnected }) {
  const { selectedCaretaker, startDate, endDate, results } = draft;
  const setSelectedCaretaker = (v) => onDraftChange(d => ({ ...d, selectedCaretaker: v }));
  const setStartDate         = (v) => onDraftChange(d => ({ ...d, startDate: v }));
  const setEndDate           = (v) => onDraftChange(d => ({ ...d, endDate: v }));
  const setResults           = (v) => onDraftChange(d => ({ ...d, results: v }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [qbLoading, setQbLoading] = useState(false);
  const [qbStatus, setQbStatus] = useState('');

  const activeCaretakers = (caretakerList ?? [])
    .filter(c => !c.isDeactivated)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleGenerate = async () => {
    if (!selectedCaretaker) { setError('Please select a caretaker.'); return; }
    if (!startDate)          { setError('Please select a start date.'); return; }
    if (!endDate)            { setError('Please select an end date.'); return; }
    if (endDate < startDate) { setError('End date must be after start date.'); return; }

    setError('');
    setLoading(true);
    setResults(null);

    try {
      const startMs = new Date(startDate).getTime();
      const endMs   = new Date(endDate).getTime() + 24 * 60 * 60 * 1000;

      const caretaker = activeCaretakers.find(c => c.name === selectedCaretaker);

      const rawShifts = await ShiftsService.getShiftsForCaretakerInRange(caretaker.email, startMs, endMs);
      const shifts = rawShifts.map(mapShiftRow);

      // Gather all unique clients so we can fetch care reports per client
      const uniqueClients = [...new Set(shifts.map(s => s.client))];
      const allReports = (await Promise.all(
        uniqueClients.map(c => CareReportSubmissionService.getReportsForClient(c))
      )).flat();

      const shiftRows = shifts
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .map(s => {
          const shiftMs = new Date(s.start).getTime();
          const client = (clientList ?? []).find(c => c.name === s.client);
          const rate = s.isFlatFee
            ? null
            : determinePriceByDate(caretaker.pay, shiftMs);
          const mins = s.isFlatFee ? 0 : minsInSpanEdited([s], { excludeCleaning: false });
          const hours = s.isFlatFee ? null : parseFloat(hoursInSpan(mins));
          const lineTotal = s.isFlatFee ? null : parseFloat((hours * rate).toFixed(2));
          const flatFee = s.isFlatFee
            ? (Number(s.flatFeeAmount) || Number(s.flatFeeAmountClient) || 0)
            : null;
          const mileage = Number(s.shiftMileage) || 0;

          // Match care report by shift_id (UUID) — timestamp matching is unreliable
          const report = allReports.find(r => r.shift_id === s.docID);
          const reimbursement = report ? (Number(report.supplies_amount) || 0) : 0;

          return {
            id: s.docID,
            start: s.start,
            end: s.editedEndTime || s.end,
            client: s.client,
            isFlatFee: s.isFlatFee,
            hours,
            rate,
            lineTotal,
            flatFee,
            mileage,
            reimbursement,
            // raw shift fields for editing
            _raw: s,
            _report: report ?? null,
          };
        });

      const totalHoursAmt = shiftRows
        .filter(r => !r.isFlatFee)
        .reduce((sum, r) => sum + (r.lineTotal || 0), 0);
      const totalFlatFees = shiftRows.reduce((sum, r) => sum + (r.flatFee || 0), 0);
      const totalMileage  = shiftRows.reduce((sum, r) => sum + r.mileage, 0) * 0.45;
      const totalReimbursements = shiftRows.reduce((sum, r) => sum + r.reimbursement, 0);
      const grandTotal = totalHoursAmt + totalFlatFees + totalMileage + totalReimbursements;

      setResults({ shiftRows, totalHoursAmt, totalFlatFees, totalMileage, totalReimbursements, grandTotal });
    } catch (err) {
      console.error(err);
      setError('Failed to generate payroll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row.id);
    setSaveError('');
    setEditValues({
      start:         row._raw.editedStartTime || row._raw.start,
      end:           row._raw.editedEndTime   || row._raw.end,
      client:        row._raw.client,
      mileage:       row.mileage,
      flatFee:       row.flatFee ?? '',
      reimbursement: row.reimbursement,
    });
  };

  const handleSave = async () => {
    const row = results.shiftRows.find(r => r.id === editRow);
    if (!row) return;
    setSaving(true);
    setSaveError('');
    try {
      const updates = {
        edited_start_time:       editValues.start,
        edited_end_time:         editValues.end,
        client:                  editValues.client,
        shift_mileage:           Number(editValues.mileage) || 0,
      };
      if (row.isFlatFee) {
        updates.flat_fee_amount = Number(editValues.flatFee) || 0;
      }
      await ShiftsService.editShiftInfo(editRow, updates);

      // Update reimbursement on the care report if one exists or create a minimal record
      const newReimbursement = Number(editValues.reimbursement) || 0;
      if (row._report) {
        await CareReportSubmissionService.submitReport({
          ...row._report,
          supplies_amount: newReimbursement,
        });
      } else if (newReimbursement > 0) {
        await CareReportSubmissionService.submitReport({
          shift_id:           editRow,
          client_name:        editValues.client,
          caretaker_email:    row._raw.caretakerEmail,
          shift_start:        editValues.start,
          shift_end:          editValues.end,
          mileage:            0,
          supplies_description: '',
          supplies_amount:    newReimbursement,
          important_information: '',
          custom_answers:     {},
        });
      }
      setEditRow(null);
      await handleGenerate();
    } catch (err) {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fmt = n => `$${Number(n).toFixed(2)}`;

  const handleSendToQB = async () => {
    if (!results) return;
    setQbLoading(true);
    setQbStatus('');
    try {
      const caretaker = activeCaretakers.find(c => c.name === selectedCaretaker);
      const periodLabel = `${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      const result = await QuickBooksService.createVendorBill({
        caretakerName: selectedCaretaker,
        caretakerEmail: caretaker?.email ?? '',
        periodLabel,
        totals: {
          hoursAmt: results.totalHoursAmt,
          flatFees: results.totalFlatFees,
          mileage: results.totalMileage,
          reimbursements: results.totalReimbursements,
          grandTotal: results.grandTotal,
        },
      });
      setQbStatus(`✓ Vendor Bill created in QuickBooks ($${Number(result.totalAmt).toFixed(2)})`);
    } catch (err) {
      setQbStatus(`Error: ${err.message}`);
    } finally {
      setQbLoading(false);
    }
  };

  const sortedClients = [...(clientList ?? [])]
    .filter(c => !c.is_deactivated)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="payrollPageDiv">
      <div className="payrollPageHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h2 className="payrollPageTitle">Payroll</h2>
      </div>
      <div className="formSep"></div>

      <div className="payrollFormSection">
        <h3 className="payrollFormTitle">Generate Payroll</h3>

        <div className="payrollFormGrid">
          <label className="payrollLabel">Caretaker</label>
          <select
            className="payrollSelect"
            value={selectedCaretaker}
            onChange={e => setSelectedCaretaker(e.target.value)}
          >
            <option value="">— select caretaker —</option>
            {activeCaretakers.map(c => (
              <option key={c.docID} value={c.name}>{c.name}</option>
            ))}
          </select>

          <label className="payrollLabel">Start Date</label>
          <input
            type="date" className="payrollInput"
            value={startDate} onChange={e => setStartDate(e.target.value)}
          />

          <label className="payrollLabel">End Date</label>
          <input
            type="date" className="payrollInput"
            value={endDate} onChange={e => setEndDate(e.target.value)}
          />
        </div>

        {error && <p className="payrollError">{error}</p>}

        <button className="payrollGenerateBtn" onClick={handleGenerate} disabled={loading}>
          {loading ? 'Generating…' : 'Generate'}
        </button>
      </div>

      {results && (
        <>
          <div className="formSep"></div>
          <div className="payrollResultsSection">
            <h3 className="payrollResultsTitle">
              {selectedCaretaker} &mdash;{' '}
              {new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to{' '}
              {new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </h3>

            {results.shiftRows.length === 0 ? (
              <p className="payrollEmpty">No shifts found for this caretaker in the selected date range.</p>
            ) : (
              <div className="payrollTableWrapper">
                <table className="payrollTable">
                  <thead>
                    <tr>
                      <th className="prTh">Start</th>
                      <th className="prTh">End</th>
                      <th className="prTh">Client</th>
                      <th className="prTh prRight">Hours</th>
                      <th className="prTh prRight">Rate</th>
                      <th className="prTh prRight">Hours Total</th>
                      <th className="prTh prRight">Flat Fee</th>
                      <th className="prTh prRight">Mileage</th>
                      <th className="prTh prRight">Reimbursements</th>
                      <th className="prTh"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.shiftRows.map(row => (
                      <tr key={row.id} className="prRow">
                        <td className="prTd">{formatDatetime(row.start)}</td>
                        <td className="prTd">{formatDatetime(row.end)}</td>
                        <td className="prTd">{row.client}</td>
                        <td className="prTd prRight">{row.hours != null ? row.hours.toFixed(2) : '—'}</td>
                        <td className="prTd prRight">{row.rate != null ? `$${Number(row.rate).toFixed(2)}` : '—'}</td>
                        <td className="prTd prRight">{row.lineTotal != null ? fmt(row.lineTotal) : '—'}</td>
                        <td className="prTd prRight">{row.flatFee != null ? fmt(row.flatFee) : '—'}</td>
                        <td className="prTd prRight">{row.mileage > 0 ? `${row.mileage} mi` : '—'}</td>
                        <td className="prTd prRight">{row.reimbursement > 0 ? fmt(row.reimbursement) : '—'}</td>
                        <td className="prTd">
                          <button className="prEditBtn" onClick={() => openEdit(row)}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="payrollSummary">
              <div className="prSummaryRow">
                <span className="prSummaryLabel">Hours Subtotal</span>
                <span className="prSummaryValue">{fmt(results.totalHoursAmt)}</span>
              </div>
              <div className="prSummaryRow">
                <span className="prSummaryLabel">Flat Fee Shifts</span>
                <span className="prSummaryValue">{fmt(results.totalFlatFees)}</span>
              </div>
              <div className="prSummaryRow">
                <span className="prSummaryLabel">Mileage (@ $0.45/mi)</span>
                <span className="prSummaryValue">{fmt(results.totalMileage)}</span>
              </div>
              <div className="prSummaryRow">
                <span className="prSummaryLabel">Reimbursements</span>
                <span className="prSummaryValue">{fmt(results.totalReimbursements)}</span>
              </div>
              <div className="prSummaryRow prTotalRow">
                <span className="prSummaryLabel">Total</span>
                <span className="prSummaryValue">{fmt(results.grandTotal)}</span>
              </div>
            </div>

            <div className="prQbRow">
              <button
                className="prQbBtn"
                onClick={handleSendToQB}
                disabled={qbLoading || !qbConnected || results.shiftRows.length === 0}
              >
                {qbLoading ? 'Sending…' : 'Send to QuickBooks'}
              </button>
              {qbStatus && (
                <span className={qbStatus.startsWith('✓') ? 'prQbSuccess' : 'prQbError'}>
                  {qbStatus}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editRow && (
        <div className="prModalOverlay" onClick={() => setEditRow(null)}>
          <div className="prModal" onClick={e => e.stopPropagation()}>
            <h3 className="prModalTitle">Edit Shift</h3>

            <div className="prModalGrid">
              <label className="prModalLabel">Start Time</label>
              <input
                type="datetime-local" className="prModalInput"
                value={editValues.start ? editValues.start.slice(0, 16) : ''}
                onChange={e => setEditValues(v => ({ ...v, start: e.target.value }))}
              />

              <label className="prModalLabel">End Time</label>
              <input
                type="datetime-local" className="prModalInput"
                value={editValues.end ? editValues.end.slice(0, 16) : ''}
                onChange={e => setEditValues(v => ({ ...v, end: e.target.value }))}
              />

              <label className="prModalLabel">Client</label>
              <select
                className="prModalInput"
                value={editValues.client}
                onChange={e => setEditValues(v => ({ ...v, client: e.target.value }))}
              >
                {sortedClients.map(c => (
                  <option key={c.docID} value={c.name}>{c.name}</option>
                ))}
              </select>

              <label className="prModalLabel">Mileage (mi)</label>
              <input
                type="number" className="prModalInput"
                value={editValues.mileage}
                onChange={e => setEditValues(v => ({ ...v, mileage: e.target.value }))}
              />

              {results.shiftRows.find(r => r.id === editRow)?.isFlatFee && (
                <>
                  <label className="prModalLabel">Flat Fee ($)</label>
                  <input
                    type="number" className="prModalInput"
                    value={editValues.flatFee}
                    onChange={e => setEditValues(v => ({ ...v, flatFee: e.target.value }))}
                  />
                </>
              )}

              <label className="prModalLabel">Reimbursements ($)</label>
              <input
                type="number" className="prModalInput"
                value={editValues.reimbursement}
                onChange={e => setEditValues(v => ({ ...v, reimbursement: e.target.value }))}
              />
            </div>

            {saveError && <p className="prModalError">{saveError}</p>}

            <div className="prModalActions">
              <button className="prModalCancel" onClick={() => setEditRow(null)}>Cancel</button>
              <button className="prModalSave" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
